import { useEffect, useMemo, useState } from "react"
import { api } from "../env"
import MTable, { TABLE_STYLE } from "./mtable"
import Loading from "./loading"
import ErrorFrame from "./error"
import StarRating from "./StarRating"

const reviewsLabel = (n) => `${n} ${n === 1 ? "review" : "reviews"}`

const formatDate = (date) =>
    new Date(date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })

function RatingSummary({ average, count }) {
    if (!count) return <p className="card-label neutral">No reviews yet</p>
    return (
        <div className="flex center gap-xs">
            <StarRating value={Math.round(average)} size={16} />
            <p className="card-body medium">{average.toFixed(1)}</p>
            <p className="card-label neutral">({reviewsLabel(count)})</p>
        </div>
    )
}

// Rating cards, one per spark in use. Scoping is done by the backend:
// managers get their company's programs, admins get everything.
export default function SparkRatings({ showCompany = false }) {
    return (
        <MTable
            path="sparks/ratings"
            // Keyed by spark so a card's expanded state doesn't carry over to a different spark on the next page
            customUI={(item) => <SparkRatingCard key={item.sparkId} spark={item} showCompany={showCompany} />}
            emptyState={
                <div className="dashed-card standard-padding">
                    <p className="card-title">No sparks are used in any programs yet</p>
                </div>
            }
        />
    )
}

function SparkRatingCard({ spark, showCompany }) {
    const [expanded, setExpanded] = useState(false)

    return (
        <div className="flex columns gap-s">
            <div className="flex space-between center gap-s wrap">
                <div className="flex columns gap-xxs">
                    <p className="card-title">{spark.displayName}</p>
                    <p className="card-label neutral">
                        Used in {spark.programsUsedIn} {spark.programsUsedIn === 1 ? "program" : "programs"}
                    </p>
                </div>
                <div className="flex center gap-s">
                    <RatingSummary average={spark.averageRating} count={spark.reviewCount} />
                    <button
                        className="with-icon icon outline"
                        onClick={() => setExpanded(e => !e)}
                        aria-label={`${expanded ? "Hide" : "Show"} ratings for ${spark.displayName}`}
                        aria-expanded={expanded}
                    >
                        <span className="material-symbols-outlined scale-s">{expanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}</span>
                    </button>
                </div>
            </div>
            {expanded && <SparkRatingBreakdown sparkId={spark.sparkId} showCompany={showCompany} />}
        </div>
    )
}

function SparkRatingBreakdown({ sparkId, showCompany }) {
    const [data, setData] = useState(null)
    const [state, setState] = useState("loading")
    const [retryToken, setRetryToken] = useState(0)

    useEffect(() => {
        const controller = new AbortController()
        setState("loading")
        api.get(`sparks/${sparkId}/ratings`, { signal: controller.signal })
            .then(res => { setData(res.data); setState("showing") })
            .catch(() => { if (!controller.signal.aborted) setState("error") })
        return () => controller.abort()
    }, [sparkId, retryToken])

    if (state === "loading") return <Loading />
    if (state === "error") return <ErrorFrame onAction={() => setRetryToken(t => t + 1)} />

    return (
        <div className="flex columns gap-xs showing">
            {data.programs.map(program => (
                <ProgramRatingRow key={program.programId} sparkId={sparkId} program={program} showCompany={showCompany} />
            ))}
        </div>
    )
}

function ProgramRatingRow({ sparkId, program, showCompany }) {
    const [showComments, setShowComments] = useState(false)
    // MTable refetches whenever queryParams changes identity, so keep it stable
    const queryParams = useMemo(() => ({ programId: program.programId }), [program.programId])

    return (
        <div className="outline-card standard-padding-s flex columns gap-xs">
            <div className="flex space-between center gap-s wrap">
                <div className="flex columns gap-xxs">
                    <p className="card-body medium">{program.programName}</p>
                    {showCompany && <p className="card-label neutral">{program.companyName}</p>}
                </div>
                <div className="flex center gap-s">
                    <RatingSummary average={program.averageRating} count={program.reviewCount} />
                    {program.commentCount > 0 && (
                        <button className="text" onClick={() => setShowComments(s => !s)}>
                            {showComments ? "Hide comments" : `Show comments (${program.commentCount})`}
                        </button>
                    )}
                </div>
            </div>
            {showComments && (
                <MTable
                    path={`sparks/${sparkId}/ratings/comments`}
                    queryParams={queryParams}
                    customUI={(comment) => <RatingComment key={comment.id} comment={comment} />}
                    style={TABLE_STYLE.OUTLINE}
                    autoHideNavigation
                    emptyState={<p className="card-label neutral">No written comments for this program yet.</p>}
                />
            )}
        </div>
    )
}

function RatingComment({ comment }) {
    return (
        <div className="flex columns gap-xxs">
            <div className="flex center gap-xs">
                <StarRating value={comment.rating} size={14} />
                <p className="card-label neutral">Anonymous {comment.role.toLowerCase()} · {formatDate(comment.updatedAt)}</p>
            </div>
            <p className="card-body pre-wrap">{comment.comment}</p>
        </div>
    )
}